/**
 * C-03 (NC-03) + C-04 (NC-04) + C-05 · Lógica PURA de consolidación y grados.
 *
 * Justificación:
 *  - NC-03: los pesos 0.30/0.35/0.35 estaban hardcodeados en las vistas y los
 *    umbrales de color (4.5/3.5/2.5) no coincidían con la escala MAAM
 *    (4.5/4.0/3.0). Ahora los pesos llegan de configuracion_evaluaciones (API)
 *    y los umbrales son los canónicos.
 *  - NC-04: el nivel se DERIVA de la calificación según el catálogo de grados
 *    MAAM (ya no es texto libre).
 *  - C-05: funciones puras extraídas de las vistas para ser testeables (Vitest).
 */

export interface PesosConsolidacion {
  documental: number
  cuantitativa: number
  opinion: number
}

/** Ponderación institucional por defecto del MAAM (hoja CONVENCIONES). */
export const PESOS_MAAM_DEFECTO: PesosConsolidacion = {
  documental: 0.30,
  cuantitativa: 0.35,
  opinion: 0.35
}

/**
 * Grado del catálogo canónico MAAM, con el rango de nota que lo determina.
 *
 * Espejo del modelo `Grado` del backend. Los rangos son los canónicos
 * (4.50–5.00 / 4.00–4.49 / 3.00–3.99 / 0.00–2.99) y el grado se DERIVA de la
 * nota: no lo elige el calificador.
 */
export interface GradoMaam {
  id_grado?: number
  orden: number
  nombre: string
  rango_min: number
  rango_max: number
}

/**
 * Catálogo MAAM 0–5 (respaldo local; la fuente de verdad es /api/grados).
 *
 * Debe reflejar EXACTAMENTE lo que hay en la base. Le faltaba el nivel
 * "Se cumple en bajo grado" y su "No se cumple" abarcaba 0–2.99, así que
 * cualquier nota entre 1.00 y 2.99 se mostraba con un nivel distinto al que
 * calculaba el backend.
 */
export const GRADOS_MAAM_DEFECTO: GradoMaam[] = [
  { orden: 1, nombre: 'Se cumple plenamente',      rango_min: 4.5, rango_max: 5.0  },
  { orden: 2, nombre: 'Se cumple en alto grado',   rango_min: 4.0, rango_max: 4.49 },
  { orden: 3, nombre: 'Se cumple aceptablemente',  rango_min: 3.0, rango_max: 3.99 },
  { orden: 4, nombre: 'Se cumple en bajo grado',   rango_min: 1.0, rango_max: 2.99 },
  { orden: 5, nombre: 'No se cumple',              rango_min: 0.0, rango_max: 0.99 }
]

/**
 * Escala CNA 1–10 de los modelos históricos (respaldo local).
 *
 * Los cortes son los de la tabla oficial "ESCALA VALORACIÓN CNA / EQUIVALENCIA
 * ESCALA DE VALORACIÓN UDI". Estaban redondeados a un decimal (8.3, 6.5, 4.7,
 * 2.9), lo que desplazaba las fronteras: un 8.25 se mostraba como "alto grado"
 * mientras el backend lo calificaba de "plenamente".
 *
 * OJO: el ancla pertenece a la banda INFERIOR — un 8.20 todavía es alto grado.
 */
export const GRADOS_ESCALA_10: GradoMaam[] = [
  { orden: 1, nombre: 'Se cumple plenamente',           rango_min: 8.21, rango_max: 10.0 },
  { orden: 2, nombre: 'Se cumple en alto grado',        rango_min: 6.41, rango_max: 8.20 },
  { orden: 3, nombre: 'Se cumple aceptablemente',       rango_min: 4.61, rango_max: 6.40 },
  { orden: 4, nombre: 'Se cumple insatisfactoriamente', rango_min: 2.81, rango_max: 4.60 },
  { orden: 5, nombre: 'No se cumple',                   rango_min: 1.0,  rango_max: 2.80 }
]

/** Deriva el grado correspondiente según la escala configurada en el modelo. */
export function derivarGrado(
  calificacion: number | null,
  escalaMax: 5 | 10 = 5
): GradoMaam | null {
  const grados = escalaMax === 10 ? GRADOS_ESCALA_10 : GRADOS_MAAM_DEFECTO
  return derivarGradoMaam(calificacion, grados)
}

/**
 * Calcula la calificación final ponderada excluyendo calificaciones nulas
 * (renormaliza por la suma de pesos presentes — cumple RF-02).
 */
export function calcularCalificacionFinal(
  documental: number | null,
  cuantitativa: number | null,
  opinion: number | null,
  pesos: PesosConsolidacion = PESOS_MAAM_DEFECTO
): number {
  const entradas = [
    { val: documental, peso: pesos.documental },
    { val: cuantitativa, peso: pesos.cuantitativa },
    { val: opinion, peso: pesos.opinion }
  ].filter(p => p.val != null && p.val >= 0)

  if (!entradas.length) return 0

  const totalPeso = entradas.reduce((sum, p) => sum + p.peso, 0)
  if (totalPeso <= 0) return 0
  const totalVal = entradas.reduce((sum, p) => sum + (p.val as number) * p.peso, 0)
  return totalVal / totalPeso
}

/**
 * C-04: deriva el grado MAAM para una calificación 0–5 (umbral inferior
 * inclusivo, recorriendo de mejor a peor grado). Espejo de GradoMaam::paraCalificacion.
 */
export function derivarGradoMaam(
  calificacion: number | null,
  grados: GradoMaam[] = GRADOS_MAAM_DEFECTO
): GradoMaam | null {
  if (calificacion == null || isNaN(calificacion)) return null
  const ordenados = [...grados].sort((a, b) => a.orden - b.orden)
  return ordenados.find(g => calificacion >= g.rango_min) ?? null
}

/**
 * C-03: clase de color alineada con la escala canónica MAAM
 * (4.50–5.0 / 4.0–4.49 / 3.0–3.99 / 0–2.99). Antes: 4.5/3.5/2.5.
 */
export function nivelClassMaam(calificacionFinal: number): string {
  if (calificacionFinal >= 4.5) return 'nivel-excelente'
  if (calificacionFinal >= 4.0) return 'nivel-alto'
  if (calificacionFinal >= 3.0) return 'nivel-medio'
  return 'nivel-bajo'
}

/** Convierte la configuración del backend (porcentajes 0–100) a pesos 0–1. */
export function pesosDesdeConfiguracion(cfg: {
  ponderacion_documental: number
  ponderacion_cuantitativa: number
  ponderacion_opinion: number
} | null | undefined): PesosConsolidacion {
  if (!cfg) return PESOS_MAAM_DEFECTO
  return {
    documental: cfg.ponderacion_documental / 100,
    cuantitativa: cfg.ponderacion_cuantitativa / 100,
    opinion: cfg.ponderacion_opinion / 100
  }
}
